import styles from 'css/src/components/app/chat-tab/components/SystemPromptModal.module.scss';

import React from 'react';

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';

interface SystemPromptModalProps {
    showing: boolean;
    onHide: VoidFunction;
    /** The system prompt currently in effect for the conversation. */
    systemPrompt: string;
    /** True when the prompt has been hand-edited away from the agent default. */
    isOverridden?: boolean;
    /** Applies the edited prompt to the conversation. */
    onSave: (prompt: string) => void;
    /** Restores the agent's default prompt. Only offered while overridden. */
    onReset?: () => void;
}

const SystemPromptModal: React.FC<SystemPromptModalProps> = ({
    showing,
    onHide,
    systemPrompt,
    isOverridden = false,
    onSave,
    onReset,
}) => {
    const [draft, setDraft] = React.useState(systemPrompt);

    // Re-seed the draft each time the modal opens so it always reflects the
    // prompt currently in effect, while edits in an open modal are never
    // clobbered by a background prompt refresh.
    const wasShowingRef = React.useRef(false);
    React.useEffect(() => {
        if (showing && !wasShowingRef.current) {
            setDraft(systemPrompt);
        }
        wasShowingRef.current = showing;
    }, [showing, systemPrompt]);

    const handleDraftChange = React.useCallback((e: TypeInputChangeEvent) => {
        setDraft(e.target.value);
    }, []);

    const isDirty = draft !== systemPrompt;

    const handleSave = () => {
        onSave(draft);
        onHide();
    };

    const handleReset = () => {
        onReset?.();
        onHide();
    };

    return (
        <Modal
            showing={showing}
            onHidden={onHide}
            title="System prompt"
            size={MODAL_SIZES.LG}
            scrollable
        >
            <div
                className={`d-flex align-items-center gap-1 color-grey-500 mb-l5-8 ${styles['session-note']}`}
            >
                <i
                    translate="no"
                    className={`material-symbols-outlined ${styles['session-note-icon']}`}
                    aria-hidden
                >
                    info
                </i>
                Edits apply to this session only — reloading the page restores the default prompt.
            </div>
            {isOverridden && (
                <div className="mb-l5-8">
                    <span className="badge bg-warning text-dark">
                        edited for this session — the agent&apos;s default prompt is not being used
                    </span>
                </div>
            )}
            <TextInput
                variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                lines={20}
                text={draft}
                onChange={handleDraftChange}
                className={`font-monospace ${styles['prompt-textarea']}`}
            />
            <div className="d-flex align-items-center gap-2 mt-l5-8">
                <Button
                    text="Save"
                    size={BUTTON_SIZES.SM}
                    variant={BUTTON_VARIANTS.FILLED}
                    color={BUTTON_COLORS.PRIMARY}
                    onClick={handleSave}
                    disabled={!isDirty || !draft.trim()}
                />
                <Button
                    text="Cancel"
                    size={BUTTON_SIZES.SM}
                    variant={BUTTON_VARIANTS.OUTLINE}
                    color={BUTTON_COLORS.SECONDARY}
                    onClick={onHide}
                />
                {isOverridden && onReset && (
                    <Button
                        text="Reset to default"
                        size={BUTTON_SIZES.SM}
                        variant={BUTTON_VARIANTS.TEXT}
                        color={BUTTON_COLORS.SECONDARY}
                        onClick={handleReset}
                    />
                )}
                <span className={`ms-l5-auto color-grey-500 ${styles['char-count']}`}>
                    {draft.length.toLocaleString()} chars
                </span>
            </div>
        </Modal>
    );
};

export default SystemPromptModal;
